import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  onboardingDataMigrationApi,
  postFuzzyMatch,
  getSuggestions,
  updateFuzzyMapping,
  applyTransformation,
  removeTransformation,
  getTransformationPreview,
  saveAsTemplate,
  getSavedTemplates,
  getTemplateDetail,
  finalizeStage2,
  getRelationTabs,
  getGroupedRelationData,
  getNullRowsData,
  assignRelation,
  getCategoryOptions,
  getPlanOptions,
  finalizeStage4,
  getValidationErrors,
  getErrorDetail,
  applyFormatFixes,
  finalizeStage3,
  getConsumerStatusOptions,
  getMeterTypeOptions,
  getMeterManufacturerOptions,
  getMeterModelOptions,
  getUtilityServicesOptions,
  getValidationSummary,
  getDepreciationScheduleOptions,
  getTerritoryZoneOptions,
  getUtilityServicesAXOptions,
  getFacilityTypeOptions,
  getOperationalHoursOptions,
  getSystemTypeOptions,
  getBackupStatusOptions,
  getFacilityStatusOptions,
  getNetworkTypeOptions,
  getDepreciationMethodOptions,
  getManufacturerOptionsAX,
  getAssetTypeOptions,
  getAssetClassOptions,
  getPremiseOptions,
  getOperationalStatusOptions,
  deleteMigrationTemplate,
  getSystemStatusOptions,
  getAssetStatusOptions,
} from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@shared/hooks/use-toast";
import { AssignRelationPayload } from "./types";
import { useMemo } from "react";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { axAssetRegistry } from "@features/ax/assetRegistry/facilities/api";

export const useMigrationModules = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.migrationModules(),
    () => onboardingDataMigrationApi.getMigrationModules(remoteUtilityId)
  );
};
export const useDataTypesByModule = (
  moduleKey: number,
  enabled: boolean = true
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.dataTypesByModule(moduleKey),
    () =>
      onboardingDataMigrationApi.getDataTypesByModule(
        moduleKey,
        remoteUtilityId
      ),
    { enabled }
  );
};

// Hook to get all data migration types
export const useUpdationDataTypes = (categoryKey: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.dataUpdationTypes(categoryKey),
    () =>
      onboardingDataMigrationApi.getUpdationDataTypes(
        categoryKey,
        remoteUtilityId
      )
  );
};

// Hook to get all data migration types
export const useDataHistory = (
  page: number = DEFAULT_PAGE_NUMBER,
  limit: number = PAGE_SIZE,
  search: string = ""
) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.dataHistory(page, limit, search),
    () =>
      onboardingDataMigrationApi.getDataHistory(utilityId, page, limit, search),
    { enabled: !!utilityId }
  );
};

export const useFuzzyMatch = () => {
  return useSmartMutation(
    (params: {
      sessionId: string;
      dataType: number;
      operationType?: number;
      selectedFields?: string[];
    }) =>
      postFuzzyMatch(
        params.sessionId,
        params.dataType,
        params.operationType,
        params.selectedFields
      )
  );
};

// Hook to get suggestions with automatic polling
export const useSuggestions = (sessionId: string, enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.suggestions(sessionId),
    () => getSuggestions(sessionId),
    {
      enabled: !!sessionId && enabled,
      staleTime: 0,
    }
  );
};

// Hook to update column mapping
export const useUpdateMapping = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    (params: {
      sessionId: string;
      targetField: string;
      sourceColumn: string;
      confidence: number;
    }) =>
      updateFuzzyMapping(params.sessionId, {
        targetField: params.targetField,
        sourceColumn: params.sourceColumn,
        confidence: params.confidence,
      }),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.suggestions(
            variables.sessionId
          ),
        });
      },
    }
  );
};

// Hook to apply transformation
export const useApplyTransformation = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    (params: {
      sessionId: string;
      targetField: string;
      transformation: {
        type: string;
        order: number;
        config: Record<string, any>;
      };
    }) => applyTransformation(params.sessionId, params)
  );
};

// Hook to remove transformation
export const useRemoveTransformation = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    (params: { sessionId: string; targetField: string; order: number }) =>
      removeTransformation(params.sessionId, params)
  );
};

// Hook to get transformation preview with polling
export const useTransformationPreview = (
  sessionId: string,
  sourceColumn: string,
  enabled: boolean = false
) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.transformationPreview(
      sessionId,
      sourceColumn
    ),
    () => getTransformationPreview(sessionId, sourceColumn),
    {
      enabled: !!sessionId && !!sourceColumn && enabled,
    }
  );
};
export const useSaveTemplate = () => {
  return useSmartMutation(
    (params: {
      sessionId: string;
      templateName: string;
      description?: string;
    }) =>
      saveAsTemplate(params.sessionId, params.templateName, params.description),
    {
      onSuccess: (data) => {
        toast({
          title: "Success",
          description: `Template saved successfully: ${data}`,
        });
      },
      onError: (error) => {
        toast({
          title: `Failed to save template: ${error.message}`,
          description: "",
        });
      },
    }
  );
};

export const useSavedTemplates = (
  page: number = DEFAULT_PAGE_NUMBER,
  limit: number = PAGE_SIZE,
  search: string = ""
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.savedTemplates(page, limit, search),
    () => getSavedTemplates(page, limit, search, remoteUtilityId)
  );
};

export const useTemplateDetail = (
  templateId: string,
  enabled: boolean = true
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.templateDetail(templateId),
    () => getTemplateDetail(templateId, remoteUtilityId),
    { enabled: !!templateId && enabled }
  );
};

export const useMigrationHistorySummary = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.dashboard(remoteUtilityId),
    () =>
      onboardingDataMigrationApi.getMigrationHistorySummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
export const useFinalizeStage2 = () => {
  return useSmartMutation((sessionId: string) => finalizeStage2(sessionId));
};

export const useFinalizeStage3 = () => {
  return useSmartMutation((params: { sessionId: string; confirmed: boolean }) =>
    finalizeStage3(params.sessionId, {
      confirmed: params.confirmed,
    })
  );
};

export const useRelationTabs = (sessionId: string, enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.relationTabs(sessionId),
    () => getRelationTabs(sessionId),
    { enabled: !!sessionId && enabled }
  );
};

export const useGroupedRelationData = (
  sessionId: string,
  tabName: string,
  filters?: Record<string, any>,
  enabled: boolean = true
) => {
  // Memoize filters to prevent unnecessary re-renders
  const stableFilters = useMemo(() => {
    return { ...filters };
  }, [JSON.stringify(filters)]); // Use JSON.stringify for deep comparison

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.groupedRelationData(
      sessionId,
      tabName,
      stableFilters
    ),
    () => getGroupedRelationData(sessionId, tabName, stableFilters),
    { enabled: !!sessionId && !!tabName && enabled }
  );
};

export const useNullRowsData = (
  sessionId: string,
  tabName: string,
  page: number = 1,
  pageSize: number = 50,
  filters?: Record<string, any>,
  enabled: boolean = true
) => {
  // Memoize filters to prevent unnecessary re-renders
  const stableFilters = useMemo(() => {
    return { ...filters };
  }, [JSON.stringify(filters)]); // Use JSON.stringify for deep comparison

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.nullRowsData(
      sessionId,
      tabName,
      page,
      stableFilters
    ),
    () => getNullRowsData(sessionId, tabName, page, pageSize, stableFilters),
    { enabled: !!sessionId && !!tabName && enabled }
  );
};

export const useAssignRelation = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    (params: { sessionId: string; payload: AssignRelationPayload }) =>
      assignRelation(params.sessionId, params.payload),
    {
      onSuccess: (data, variables) => {
        toast({
          title: "Success",
          description: data.message || "Relation assigned successfully",
        });

        const tabName = variables.payload.tabName;

        // Invalidate related queries
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.relationTabs(
            variables.sessionId
          ),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.groupedRelationData(
            variables.sessionId,
            variables.payload.tabName,
            {}
          ),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.nullRowsData(
            variables.sessionId,
            tabName,
            1 // Could be any page, invalidate all
          ),
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error",
          description: error?.message || "Failed to assign relation",
        });
      },
    }
  );
};
// Hook to get category/subcategory options
export const useCategoryOptions = (
  configLevel: string,
  enabled: boolean = true
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.categoryOptions(
      remoteUtilityId,
      configLevel
    ),
    () => getCategoryOptions(remoteUtilityId, configLevel),
    { enabled: !!remoteUtilityId && !!configLevel && enabled }
  );
};

// Hook to get plan options
export const usePlanOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planOptions(remoteUtilityId),
    () => getPlanOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};

export const useFinalizeStage4 = () => {
  return useSmartMutation(
    (params: {
      sessionId: string;
      confirmed: boolean;
      proceedWithErrors: boolean;
    }) =>
      finalizeStage4(params.sessionId, {
        confirmed: params.confirmed,
        proceedWithErrors: params.proceedWithErrors,
      })
  );
};

export const useValidationErrors = (
  sessionId: string,
  enabled: boolean = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.validationErrors(sessionId),
    () => getValidationErrors(sessionId),
    {
      enabled: !!sessionId && enabled,
      refetchInterval: false,
    }
  );
};

export const useErrorDetail = (
  sessionId: string,
  errorType: string,
  field: string,
  enabled: boolean = false
) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.errorDetail(sessionId, errorType, field),
    () => getErrorDetail(sessionId, errorType, field),
    { enabled: !!sessionId && !!errorType && !!field && enabled }
  );
};

export const useApplyFormatFixes = () => {
  const queryClient = useQueryClient();

  return useSmartMutation((sessionId: string) => applyFormatFixes(sessionId), {
    onSuccess: (data, sessionId) => {
      toast({
        title: "Success",
        description: "Auto-fixing errors in progress...",
      });

      // Invalidate validation errors to refetch after fix
      queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.onboarding.validationErrors(sessionId),
      });

      queryClient.invalidateQueries({
        queryKey:
          QueryKeyFactory.module.onboarding.validationSummary(sessionId),
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Failed to apply fixes",
      });
    },
  });
};

export const useConsumerStatusOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.consumerStatusOptions(remoteUtilityId),
    () => getConsumerStatusOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};

export const useSystemStatusOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.systemStatusOptions(remoteUtilityId),
    () => getSystemStatusOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};

export const useAssetStatusOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.assetStatusOptions(remoteUtilityId),
    () => getAssetStatusOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};

export const useMeterTypeOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.meterTypeOptions(),
    () => getMeterTypeOptions(),
    { enabled }
  );
};

export const useMeterManufacturerOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.meterManufacturerOptions(remoteUtilityId),
    () => getMeterManufacturerOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};

export const useMeterModelOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.meterModelOptions(remoteUtilityId),
    () => getMeterModelOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};
export const useUtilityServicesOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.utilityServicesOptions(remoteUtilityId),
    () => getUtilityServicesOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};
export const useValidationSummary = (uploadId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.validationSummary(uploadId),

    () => getValidationSummary(uploadId)
  );
};

// Asset Class
export const useAssetClassOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.assetClassOptions(remoteUtilityId),
    () => getAssetClassOptions(remoteUtilityId, "asset_class", true),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Asset Type
export const useAssetTypeOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.assetTypeOptions(remoteUtilityId),
    () => getAssetTypeOptions(remoteUtilityId, "asset_type"),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Manufacturer
export const useManufacturerAXOptions = (
  enabled: boolean = true,
  page: number,
  limit: number
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.manufacturerOptions(
      remoteUtilityId,
      page,
      limit
    ),
    () => getManufacturerOptionsAX(remoteUtilityId, page, limit),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Depreciation Method
export const useDepreciationMethodOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.depreciationMethodOptions(),
    () => getDepreciationMethodOptions(),
    { enabled }
  );
};

// Network Type
export const useNetworkTypeOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.networkTypeOptions(),
    () => getNetworkTypeOptions(),
    { enabled }
  );
};

// Facility Status
export const useFacilityStatusOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.facilityStatusOptions(),
    () => getFacilityStatusOptions(),
    { enabled }
  );
};

// Backup Status
export const useBackupStatusOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.backupStatusOptions(),
    () => getBackupStatusOptions(),
    { enabled }
  );
};

// System Type
export const useSystemTypeOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.systemTypeOptions(remoteUtilityId),
    () => getSystemTypeOptions(remoteUtilityId, "system_type", true),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Operational Hours
export const useOperationalHoursOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.operationalHoursOptions(),
    () => getOperationalHoursOptions(),
    { enabled }
  );
};

export const useOperationalStatusOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.operationalStatusOptions(),
    () => getOperationalStatusOptions(),
    { enabled }
  );
};

// Facility Type
export const useFacilityTypeOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.facilityTypeOptions(remoteUtilityId),
    () => getFacilityTypeOptions(remoteUtilityId, "facility_type", true),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Utility Services AX
export const useUtilityServicesAXOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.utilityServicesOptions(
      remoteUtilityId
    ),
    () => getUtilityServicesAXOptions(remoteUtilityId),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Territory Zone
export const useTerritoryZoneOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.territoryZoneOptions(remoteUtilityId),
    () => getTerritoryZoneOptions(remoteUtilityId, "zone"),
    { enabled: !!remoteUtilityId && enabled }
  );
};

// Depreciation Schedule
export const useDepreciationScheduleOptions = (enabled: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.ax.depreciationScheduleOptions(),
    () => getDepreciationScheduleOptions(),
    { enabled }
  );
};

export const useManagers = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.managers(remoteUtilityId),
    () => axAssetRegistry.getFacilityManagers(remoteUtilityId),
    {
      enabled: !!remoteUtilityId && enabled,
    }
  );
};

export const usePremiseOptions = (enabled: boolean = true) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.premiseOptions(remoteUtilityId),
    () => getPremiseOptions(remoteUtilityId, true),
    { enabled: !!remoteUtilityId && enabled }
  );
};

export const useDeleteMigrationTemplate = (
  page?: number,
  limit?: number,
  search?: string
) => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    (templateId: string | number) => deleteMigrationTemplate(templateId),
    {
      onSuccess: (data) => {
        toast({
          title: "Success",
          description: "Template deleted successfully.",
        });

        // ✅ Invalidate only the specific query for current page & search
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.savedTemplates(
            page,
            limit,
            search
          ),
        });
      },
      onError: (error) => {
        toast({
          title: "Failed to delete template",
          description: error?.message || "An unexpected error occurred.",
        });
      },
    }
  );
};
